/**
 * order (발주) orval 래퍼
 */
import { customInstance } from "../custom-instance";
import { DefaultApprovalStatusSchema, ProjectInfoSchema, UserInfoSchema, } from "./default-schemes";
import { ORDER_APPROVAL_PHASE } from "@erp/shared/types/order-approval-phase.types";
import { validateArrayWithSchema, validateWithSchema, } from "@erp/shared/utils/https/zod-validation";
import qs from "qs";
import { z } from "zod";
const ORDER_URLS = {
    commercial: {
        confirm: "/order/confirm/commercial",
        confirmWithProduct: (id) => `/order/confirm/commercial/${id}/withProduct`,
        productsForInbound: (id) => `/order/confirm/commercial/${id}/products-for-inbound`,
        approval: (segment, id) => `/order/confirm/commercial/${segment}/${id}`,
        product: "/order/product/commercial",
    },
    research: {
        confirm: "/order/confirm/research",
        confirmWithProduct: (id) => `/order/confirm/research/${id}/withProduct`,
        productsForInbound: (id) => `/order/confirm/research/${id}/products-for-inbound`,
        approval: (segment, id) => `/order/confirm/research/${segment}/${id}`,
        product: "/order/product/research",
    },
};
// ── 유틸 ──
export function isValidOrderId(id) {
    return typeof id === "number" && id > 0;
}
// ── 스키마 & 타입 ──
export const GetOrderConfirmResponseSchema = z
    .object({
    id: z.number(),
    ...DefaultApprovalStatusSchema.shape,
    approvalPhase: z.nativeEnum(ORDER_APPROVAL_PHASE),
    createrOfficePhoneNumber: z.string().nullable(),
    createrPhoneNumber: z.string().nullable(),
    checkerOfficePhoneNumber: z.string().nullable(),
    checkerPhoneNumber: z.string().nullable(),
    purchaseManager: z.coerce.number().nullable(),
    purchaseManagerName: z.string().nullable(),
    purchaseManagerId: z.string().nullable(),
    paymentManager: z.coerce.number().nullable(),
    paymentManagerName: z.string().nullable(),
    paymentManagerUserId: z.string().nullable(),
    inboundPhase: z.string(),
    transferPhase: z.string(),
    projectId: z.number().nullable(),
    projectCode: z.string().nullable(),
    dueDate: z.string().nullable(),
    orderDate: z.string().nullable(),
    companyName: z.string().nullable(),
    companyManager: z.string().nullable(),
    companyRegistrationNumber: z.string().nullable(),
    companyAddress: z.string().nullable(),
    companyPhone: z.string().nullable(),
    companyFax: z.string().nullable(),
    paymentTerms: z.string().nullable(),
    accountNumber: z.string().nullable(),
    orderConfirmationFile: z.string().nullable(),
    businessRegistrationFile: z.string().nullable(),
    bankbookFile: z.string().nullable(),
    firstBill: z.string().nullable(),
    firstBillDate: z.string().nullable(),
    secondBill: z.string().nullable(),
    secondBillDate: z.string().nullable(),
    thirdBill: z.string().nullable(),
    thirdBillDate: z.string().nullable(),
    firstConfirmTransfer: z.string().nullable(),
    secondConfirmTransfer: z.string().nullable(),
    thirdConfirmTransfer: z.string().nullable(),
    approvalDocumentId: z.number().nullable(),
    approvalDocumentNumber: z.string().nullable(),
    _creater: UserInfoSchema,
    _checker: UserInfoSchema.nullable(),
    _approver: UserInfoSchema.nullable(),
    _purchaseManager: UserInfoSchema.nullable(),
    _paymentManager: UserInfoSchema.nullable(),
    _rejecter: UserInfoSchema.nullable(),
    project: ProjectInfoSchema.nullable(),
})
    .strict();
export const GetOrderConfirmWithProductResponseSchema = z
    .object({
    ...GetOrderConfirmResponseSchema.shape,
    productCalcJson: z.string(),
})
    .strict();
// ── 스키마 & 타입 (OrderProduct) ──
export const GetOrderProductResponseSchema = z.object({
    createdTime: z.string(),
    modifiedTime: z.string(),
    creater: z.coerce.number(),
    modifier: z.coerce.number(),
    deletedYn: z.number(),
    id: z.coerce.number(),
    orderId: z.coerce.number(),
    calcJson: z.string(),
});
// ── 스키마 & 타입 (OrderProduct 상세 — 발주 상세 내역용) ──
export const GetOrderProductDetailResponseSchema = z.object({
    id: z.coerce.number(),
    orderId: z.coerce.number(),
    productCode: z.string().nullable(),
    name: z.string().nullable(),
    model: z.string().nullable(),
    spec: z.string().nullable(),
    purchaseNum: z.coerce.number(),
    unitPrice: z.coerce.number(),
    purchaseEstimateFile: z.string().nullable(),
    productId: z.coerce.number().nullable(),
    classificationId: z.coerce.number().nullable(),
    classification: z.string().nullable(),
    manufacturerId: z.coerce.number().nullable(),
    manufacturer: z.string().nullable(),
    orderApprovalPhase: z.string().nullable(),
    pendingInboundNum: z.coerce.number(),
    completeInboundNum: z.coerce.number(),
});
// ── API 함수 ──
function createGetOrderConfirmWithProduct(type) {
    return async (id) => {
        const data = await customInstance({
            url: ORDER_URLS[type].confirmWithProduct(id),
            method: "GET",
        });
        return validateWithSchema(GetOrderConfirmWithProductResponseSchema, data);
    };
}
export const getCommercialOrderConfirmWithProduct = createGetOrderConfirmWithProduct("commercial");
export const getResearchOrderConfirmWithProduct = createGetOrderConfirmWithProduct("research");
function createGetOrderConfirm(type) {
    return async (params) => {
        const data = await customInstance({
            url: ORDER_URLS[type].confirm,
            method: "GET",
            params,
            paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
        });
        return validateArrayWithSchema(GetOrderConfirmResponseSchema, data);
    };
}
export const getCommercialOrderConfirm = createGetOrderConfirm("commercial");
export const getResearchOrderConfirm = createGetOrderConfirm("research");
function createGetOrderProduct(type) {
    return async (orderId) => {
        const data = await customInstance({
            url: ORDER_URLS[type].product,
            method: "GET",
            params: { orderId },
        });
        return validateArrayWithSchema(GetOrderProductResponseSchema, data);
    };
}
export const getCommercialOrderProduct = createGetOrderProduct("commercial");
export const getResearchOrderProduct = createGetOrderProduct("research");
function createGetOrderProductDetail(type) {
    return async (orderId) => {
        const data = await customInstance({
            url: ORDER_URLS[type].product,
            method: "GET",
            params: { orderId },
        });
        return validateArrayWithSchema(GetOrderProductDetailResponseSchema, data);
    };
}
export const getCommercialOrderProductDetail = createGetOrderProductDetail("commercial");
export const getResearchOrderProductDetail = createGetOrderProductDetail("research");
function createPostOrderConfirm(type) {
    return async (body) => {
        return customInstance({
            url: ORDER_URLS[type].confirm,
            method: "POST",
            headers: { "Content-Type": "application/json" },
            data: body,
        });
    };
}
export const postCommercialOrderConfirm = createPostOrderConfirm("commercial");
export const postResearchOrderConfirm = createPostOrderConfirm("research");
function createPatchOrderConfirm(type) {
    return async (orderId, body) => {
        return customInstance({
            url: `${ORDER_URLS[type].confirm}/${orderId}`,
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            data: body,
        });
    };
}
export const patchCommercialOrderConfirm = createPatchOrderConfirm("commercial");
export const patchResearchOrderConfirm = createPatchOrderConfirm("research");
function createPatchOrderConfirmApprove(type) {
    return async (id, segment, rejectReason) => {
        return customInstance({
            url: ORDER_URLS[type].approval(segment, id),
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            data: { rejectReason },
        });
    };
}
export const patchCommercialOrderConfirmApprove = createPatchOrderConfirmApprove("commercial");
export const patchResearchOrderConfirmApprove = createPatchOrderConfirmApprove("research");
// ── 스키마 & 타입 (ProductsForInbound) ──
export const ProductForInboundItemSchema = z.object({
    orderProductId: z.number(),
    approvalDocumentItemId: z.number(),
    itemName: z.string(),
    itemNumber: z.number(),
    productCode: z.string().nullable(),
    spec: z.string().nullable(),
    model: z.string().nullable(),
    unitPrice: z.number(),
    quantity: z.number(),
    supplyAmount: z.number(),
    classificationId: z.number().nullable(),
    classificationName: z.string().nullable(),
    manufacturerId: z.number().nullable(),
    manufacturerName: z.string().nullable(),
    purchaseEstimateFile: z.string().nullable(),
    groupName: z.string().nullable(),
    inboundedQuantity: z.number(),
    remainingQuantity: z.number(),
});
export const ProductsForInboundResponseSchema = z.object({
    orderId: z.number(),
    approvalDocumentId: z.number().nullable(),
    items: z.array(ProductForInboundItemSchema),
});
function createGetProductsForInbound(type) {
    return async (orderId) => {
        const data = await customInstance({
            url: ORDER_URLS[type].productsForInbound(orderId),
            method: "GET",
        });
        return validateWithSchema(ProductsForInboundResponseSchema, data);
    };
}
export const getCommercialProductsForInbound = createGetProductsForInbound("commercial");
export const getResearchProductsForInbound = createGetProductsForInbound("research");
// ── Phase 1 API: submit / confirm / payment-complete / delete ──
function createPatchOrderConfirmSubmit(type) {
    return async (id) => {
        return customInstance({
            url: `${ORDER_URLS[type].confirm}/submit/${id}`,
            method: "PATCH",
        });
    };
}
export const patchCommercialOrderConfirmSubmit = createPatchOrderConfirmSubmit("commercial");
export const patchResearchOrderConfirmSubmit = createPatchOrderConfirmSubmit("research");
function createPatchOrderConfirmConfirm(type) {
    return async (id) => {
        return customInstance({
            url: `${ORDER_URLS[type].confirm}/confirm/${id}`,
            method: "PATCH",
        });
    };
}
export const patchCommercialOrderConfirmConfirm = createPatchOrderConfirmConfirm("commercial");
export const patchResearchOrderConfirmConfirm = createPatchOrderConfirmConfirm("research");
function createPatchOrderConfirmPaymentComplete(type) {
    return async (id) => {
        return customInstance({
            url: `${ORDER_URLS[type].confirm}/payment-complete/${id}`,
            method: "PATCH",
        });
    };
}
export const patchCommercialOrderConfirmPaymentComplete = createPatchOrderConfirmPaymentComplete("commercial");
export const patchResearchOrderConfirmPaymentComplete = createPatchOrderConfirmPaymentComplete("research");
function createDeleteOrderConfirm(type) {
    return async (id) => {
        return customInstance({
            url: `${ORDER_URLS[type].confirm}/${id}`,
            method: "DELETE",
        });
    };
}
export const deleteCommercialOrderConfirm = createDeleteOrderConfirm("commercial");
export const deleteResearchOrderConfirm = createDeleteOrderConfirm("research");
// ── SDK 객체 ──
export const orderApi = {
    isValidOrderId,
    getCommercialOrderConfirmWithProduct,
    getResearchOrderConfirmWithProduct,
    getCommercialOrderConfirm,
    getResearchOrderConfirm,
    getCommercialOrderProduct,
    getResearchOrderProduct,
    getCommercialOrderProductDetail,
    getResearchOrderProductDetail,
    getCommercialProductsForInbound,
    getResearchProductsForInbound,
    postCommercialOrderConfirm,
    postResearchOrderConfirm,
    patchCommercialOrderConfirm,
    patchResearchOrderConfirm,
    patchCommercialOrderConfirmApprove,
    patchResearchOrderConfirmApprove,
};
